import React, { useEffect, useRef, useState } from 'react'
import "../../CSS/style.css";
import { Icon } from '@iconify/react/dist/iconify.js';
import Auth from '../../Api/Auth';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Loader from '../Loader';
import AccountManager from '../../Api/AccountManager';

const Login = () => {

    const [Username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [cookies, setCookie] = useCookies(['token']);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    // OTP Login states
    const [otpPanelOpen, setOtpPanelOpen] = useState(false); // controls the side panel
    const [otpStep, setOtpStep] = useState('email');          // 'email' | 'otp'
    const [otpEmail, setOtpEmail] = useState('');
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const otpRefs = useRef([]);
    const timerRef = useRef(null);

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    // ── Password Login ──────────────────────────────────────────────
    const handleLogin = async () => {
        setLoading(true);
        try {
            const response = await Auth.login(Username, password);
            if (response?.IsSuccess) {
                const { token, user } = response.Result || {};
                if (token) {
                    setCookie('token', token, { path: '/' });
                    localStorage.setItem('authToken', token);
                }
                if (user) localStorage.setItem('authUser', JSON.stringify(user));
                enqueueSnackbar('Logged in successfully!', { variant: 'success' });
                navigate('/Dashboard');
                return;
            }
            const errorMessage = Array.isArray(response?.ErrorMessages)
                ? response.ErrorMessages.join(', ')
                : response?.ErrorMessages || response?.Message || 'Invalid username or password';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } catch (error) {
            const apiErrorMessage =
                error?.response?.data?.ErrorMessages ||
                error?.response?.data?.message ||
                error?.message ||
                'Login failed. Please try again.';
            enqueueSnackbar(
                Array.isArray(apiErrorMessage) ? apiErrorMessage.join(', ') : apiErrorMessage,
                { variant: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    // ── OTP Panel helpers ───────────────────────────────────────────
    const openOtpPanel = () => {
        setOtpPanelOpen(true);
        setOtpStep('email');
        setOtpEmail('');
        setOtpValues(['', '', '', '', '', '']);
    };

    const closeOtpPanel = () => {
        setOtpPanelOpen(false);
        setOtpStep('email');
        clearInterval(timerRef.current);
    };

    const startResendTimer = (seconds = 30) => {
        setIsResendDisabled(true);
        setSecondsLeft(seconds);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setIsResendDisabled(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Step 1 — Send OTP to email
    const handleSendOtp = async () => {
        if (!otpEmail) {
            enqueueSnackbar('Please enter correct email address.', { variant: 'warning' });
            return;
        }
        setLoading(true);
        try {
            const response = await Auth.generateOtpV1(otpEmail);
            if (response?.IsSuccess) {
                enqueueSnackbar('OTP sent to your email!', { variant: 'success' });
                setOtpStep('otp');
                startResendTimer(30);
            } else {
                enqueueSnackbar('Invalid Email Id', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to send OTP.', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = () => {
        if (!isResendDisabled) handleSendOtp();
    };

    // OTP box keyboard navigation
    const handleOtpChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;           // digits only
        const updated = [...otpValues];
        updated[index] = value;
        setOtpValues(updated);
        if (value && index < 5) otpRefs.current[index + 1]?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0)
            otpRefs.current[index - 1]?.focus();
    };

    const handleOtpLogin = async () => {
        const otp = otpValues.join('');
        if (otp.length < 6) {
            enqueueSnackbar('Please enter the complete 6-digit OTP.', { variant: 'warning' });
            return;
        }
        setLoading(true);
        try {
            const response = await Auth.verifyOtpV1(otpEmail, otp);
            if (response?.IsSuccess) {
                const { token, user } = response.Result || {};
                if (token) {
                    setCookie('token', token, { path: '/' });
                    localStorage.setItem('authToken', token);
                }
                if (user) localStorage.setItem('authUser', JSON.stringify(user));
                enqueueSnackbar('Logged in successfully!', { variant: 'success' });
                navigate('/Dashboard');
                return;
            }
            enqueueSnackbar('Invalid or Expired OTP', { variant: 'error' });
        } catch (err) {
            enqueueSnackbar(err?.message || 'OTP verification failed.', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Keyboard shortcuts
    const input1Ref = useRef(null);
    const input2Ref = useRef(null);
    const handleKeyPress = (event, nextInputRef) => {
        if (event.key === 'Enter') { event.preventDefault(); nextInputRef.current.focus(); }
    };
    const handleKeyPress1 = (event) => {
        if (event.key === 'Enter') { event.preventDefault(); handleLogin(); }
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    useEffect(() => {
        setOtpPanelOpen(true);
        setOtpStep('email');
        setOtpEmail('');
        setOtpValues(['', '', '', '', '', '']);
    }, [])

    return (
        <div className="Flex-loginPage">
            {!otpPanelOpen && false && (
                <div className="form-login">
                    <p className="form-title"><u>LOGIN</u></p>

                    <div className="input-container">
                        <input
                            placeholder="Enter Username"
                            type="text"
                            value={Username}
                            onChange={(e) => setUsername(e.target.value)}
                            ref={input1Ref}
                            onKeyDown={(e) => handleKeyPress(e, input2Ref)}
                            required
                        />
                    </div>

                    <div className="input-container">
                        <input
                            placeholder="Enter Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            ref={input2Ref}
                            onKeyDown={handleKeyPress1}
                            required
                        />
                        <span onClick={togglePasswordVisibility}>
                            <Icon icon={showPassword ? 'mdi-light:eye-off' : 'mdi-light:eye'} />
                        </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '20px 0px', flexDirection: 'column' }}>
                        <button className="submit" type="button" onClick={handleLogin}>
                            Log In
                        </button>
                        <label style={{ textAlign: 'center', fontSize: '14px', padding: '5px' }}>OR</label>
                        <label
                            onClick={openOtpPanel}
                            style={{ textAlign: 'center', color: '#0c319c', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Login with OTP
                        </label>
                    </div>
                </div>
            )}

            {/* ── OTP Side Panel ─────────────────────────────────────────── */}
            {otpPanelOpen && (
                <div className="form-login" onClick={closeOtpPanel}>
                    <div
                        className={`otp-panel ${otpPanelOpen ? 'otp-panel--open' : ''}`}
                        onClick={(e) => e.stopPropagation()}   // prevent overlay-click closing
                    >
                        {/* Close button */}
                        {/* <button className="otp-panel__close" onClick={closeOtpPanel}>
                            <Icon icon="mdi:close" width={20} />
                        </button> */}

                        {otpStep === 'email' && (
                            <>
                                <p className="form-title"><u>LOGIN</u></p>
                                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, textAlign: "center" }}>
                                    Enter your registered email address to receive a OTP.
                                </p>
                                <div className="input-container">
                                    <input
                                        placeholder="Enter Email Address"
                                        type="email"
                                        value={otpEmail}
                                        onChange={(e) => setOtpEmail(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendOtp(); }}
                                        autoFocus
                                        required
                                    />
                                </div>
                                <button className="submit" style={{ marginTop: 12 }} onClick={handleSendOtp}>
                                    Send OTP
                                </button>
                            </>
                        )}

                        {otpStep === 'otp' && (
                            <>
                                <p className="form-title"><u>ENTER OTP</u></p>
                                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, textAlign: "center" }}>
                                    A 6-digit OTP was sent to <strong>technical.techcherry@gmail.com</strong>{/*<strong>{otpEmail}</strong>.*/}
                                </p>
                                <div className="OtpContainer">
                                    {otpValues.map((val, idx) => (
                                        <input
                                            key={idx}
                                            ref={(el) => (otpRefs.current[idx] = el)}
                                            className="otp-input"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={val}
                                            onChange={(e) => handleOtpChange(idx, e.target.value)}
                                            autoFocus={idx === 0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && idx === 5) {
                                                    handleOtpLogin();
                                                }
                                                handleOtpKeyDown(idx, e);
                                            }}
                                        />
                                    ))}
                                </div>

                                <p style={{ fontSize: 13, marginTop: 10, color: isResendDisabled ? '#947bdd' : '#9ca3af', textAlign: "center" }}>
                                    {isResendDisabled
                                        ? `Resend OTP after ${secondsLeft} sec`
                                        : (
                                            <span>
                                                Haven't received the OTP?{' '}
                                                <label className="resendBtn" onClick={handleResendOtp}>
                                                    Resend OTP
                                                </label>
                                            </span>
                                        )}
                                </p>

                                <button className="submit" style={{ marginTop: 16 }} onClick={handleOtpLogin}>
                                    Verify & Login
                                </button>

                                <button
                                    onClick={() => setOtpStep('email')}
                                    style={{ background: 'none', border: 'none', color: '#0c319c', fontSize: 13, marginTop: 10, cursor: 'pointer' }}
                                >
                                    ← Change Email
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {loading && <Loader />}
        </div>
    );
};

export default Login;